import { Fragment } from 'react';
import {
  FormattedRelativeTime,
  Heading,
  InlineStack,
  Link,
  Stack,
  Tag,
  Text,
  TextLabel,
  Tooltip,
} from '@customerio/pluma-components/react';
import type { Tag as WorkspaceTag } from '@customerio/hydra-ui-api';
import { capitalize } from '@/utils/string';
import { SourcesCell } from '../components/sources-cell';
import { shareOf } from '../utils/share';
import { SOURCES_TOOLTIP_CONTENT } from '../utils/sources';
import { SENSITIVE_PRIVACY_LEVEL, type DataIndexItemKind } from '../utils/types';
import { usePermissions } from '@customerio/hydra-permissions/react';
import type { DataIndexItemAddress } from '../urls';
import type { DataIndexItemDetail, ItemReach } from './hooks';
import { reachHref, type ItemPageModel } from './page-model';

/**
 * What is known about one item, beside what uses it.
 *
 * The reach line is the point of the panel: how much of the workspace the
 * item actually applies to, and a way straight to that list. Everything else
 * is provenance — when it last arrived, when it was last built on, and where
 * it comes from.
 */

/** What the panel calls the things an item applies to. */
export type ReachTerms = {
  /** "Person" or the object type's own name, singular. */
  singular: string;
  plural: string;
};

type DetailsPanelProps = {
  item: DataIndexItemDetail;
  kind: DataIndexItemKind;
  tags: readonly WorkspaceTag[];
  /** How many carry the item, and how many there are altogether. */
  usage: ItemReach;
  reachHref: string;
  terms: ReachTerms;
  /**
   * Whether the reach line is shown at all, and whether it carries a share.
   * A relationship attribute has neither; an event is performed rather than
   * carried, so it is counted but never given a percentage.
   */
  reach: { withRow: boolean; withShare: boolean };
  /** Whether the viewer may see the sensitivity of an attribute at all. */
  withSensitivity: boolean;
  /** What a workspace calls an automation, for the "last used" explanation. */
  campaignIndefinite: string;
  /** Offered where the caller can edit in place; the item page edits in a modal instead. */
  onEdit?: () => void;
};

/**
 * What the workspace knows about one item: its tags, how much of the
 * workspace carries it, whether it is redacted, and where it came from.
 *
 * The share itself is `shareOf`'s to round. A workspace with nothing in it
 * has none to report, so the line drops the parenthetical rather than
 * filling it with a dash.
 */
export function DetailsPanel({
  item,
  kind,
  tags,
  usage: reach,
  reachHref,
  terms,
  reach: reachScope,
  withSensitivity,
  campaignIndefinite,
  onEdit,
}: DetailsPanelProps) {
  const noun = kind === 'event' ? 'event' : 'attribute';

  return (
    <Stack gap="200">
      {onEdit == null ? null : (
        <InlineStack alignInline="space-between" alignBlock="center">
          <Heading level="2">Details</Heading>
          <Link variant="primary" icon="edit" iconPosition="trailing" onClick={onEdit}>
            Edit
          </Link>
        </InlineStack>
      )}
      <DetailRow label="Tags">
        <TagList tags={tags} />
      </DetailRow>

      {/* The label is capitalized and the sentence beside it is not, which is
          what the page this replaces does: `{{capitalize (pluralize 2
          usageUnit)}}` for the label, the bare plural inside "4 people (1%)
          have this attribute" (`item-details/usage/index.gts:282,295`). */}
      {reachScope.withRow ? (
        <DetailRow label={capitalize(terms.plural)}>
          <ReachLine reach={reach} href={reachHref} terms={terms} noun={noun} withShare={reachScope.withShare} />
        </DetailRow>
      ) : null}

      {withSensitivity && item.privacyLevel != null ? (
        <DetailRow
          label="Sensitive"
          tooltip="If you mark an attribute as sensitive, you can redact it from specific teammates."
        >
          <Text>{item.privacyLevel === SENSITIVE_PRIVACY_LEVEL ? 'Yes' : 'No'}</Text>
        </DetailRow>
      ) : null}

      <ProvenanceRows item={item} noun={noun} campaignIndefinite={campaignIndefinite} />
    </Stack>
  );
}

type TagListProps = {
  tags: readonly WorkspaceTag[];
};

function TagList({ tags }: TagListProps) {
  if (tags.length === 0) {
    return <Text>—</Text>;
  }

  return (
    <InlineStack gap="050" wrap="wrap">
      {tags.map((tag) => (
        <Tag key={tag.id}>{tag.name}</Tag>
      ))}
    </InlineStack>
  );
}

type ProvenanceRowsProps = {
  item: DataIndexItemDetail;
  noun: string;
  campaignIndefinite: string;
};

/** Where the item's data comes from, and when it last moved. */
function ProvenanceRows({ item, noun, campaignIndefinite }: ProvenanceRowsProps) {
  return (
    <>
      <DetailRow
        label="Last seen"
        tooltip={`This is the most recent time the system received an update for this ${noun}.`}
      >
        <RelativeOrDash at={item.lastSeenAt} />
      </DetailRow>

      {item.dailyCount != null && item.dailyCount > 0 ? (
        <DetailRow
          label="Today's count"
          tooltip="The number of times this event was received today (since midnight UTC)."
        >
          <Text>{item.dailyCount.toLocaleString()}</Text>
        </DetailRow>
      ) : null}

      <DetailRow
        // "Last Used" in the page this replaces, but `content-sentence-case`
        // forbids the capital: house style wins over the copy here.
        label="Last used"
        tooltip={`This is the most recent time this ${noun} was referenced by a user to build ${campaignIndefinite} or segment.`}
      >
        <RelativeOrDash at={item.lastUsedAt} />
      </DetailRow>

      <DetailRow label="Sources" tooltip={SOURCES_TOOLTIP_CONTENT}>
        <SourcesCell sources={item.sources} />
      </DetailRow>
    </>
  );
}

type DetailRowProps = {
  label: string;
  tooltip?: string;
  children: React.ReactNode;
};

function DetailRow({ label, tooltip, children }: DetailRowProps) {
  return (
    <Stack gap="050">
      {tooltip == null ? (
        <TextLabel>{label}</TextLabel>
      ) : (
        <Tooltip content={tooltip} placement="right" isUnderlined>
          <TextLabel>{label}</TextLabel>
        </Tooltip>
      )}
      <div>{children}</div>
    </Stack>
  );
}

type RelativeOrDashProps = {
  /** Epoch seconds, as the backend records them. */
  at: number | null;
};

function RelativeOrDash({ at }: RelativeOrDashProps) {
  if (at == null || at === 0) {
    return <Text>—</Text>;
  }
  return <FormattedRelativeTime date={at * 1000} withTooltip />;
}

type ReachLineProps = {
  reach: ItemReach;
  href: string;
  terms: ReachTerms;
  noun: string;
  withShare: boolean;
};

/** "1,204 People (38%) have this attribute", linking to that list. */
function ReachLine({ reach, href, terms, noun, withShare }: ReachLineProps) {
  if (reach.withItem == null || reach.withItem === 0) {
    return <Text>—</Text>;
  }

  const unit = reach.withItem === 1 ? terms.singular : terms.plural;
  const share = !withShare || reach.total == null ? null : shareOf(reach.withItem, reach.total);
  const verb = reach.withItem === 1 ? 'has' : 'have';
  const performed = noun === 'event' ? ' performed' : '';

  return (
    <Link href={href} variant="secondary" icon="exit" iconPosition="trailing" target="_blank">
      <Fragment>
        {reach.withItem.toLocaleString()} {unit}
        {share == null ? '' : ` (${share})`} {verb}
        {performed} this {noun}
      </Fragment>
    </Link>
  );
}

type ItemDetailsProps = {
  workspaceId: number;
  address: DataIndexItemAddress;
  item: DataIndexItemDetail;
  tags: readonly WorkspaceTag[];
  /** How many carry the item, and how many there are altogether. */
  reach: ItemReach;
  model: Pick<ItemPageModel, 'reachTerms' | 'reach' | 'campaignIndefinite'>;
};

/**
 * One item's details, wherever they are read: on its own page and beside the
 * list. Both need the same eight answers from the same two reads, so neither
 * of them works them out for itself.
 */
export function ItemDetails({ workspaceId, address, item, tags, reach, model }: ItemDetailsProps) {
  const { hasPermission } = usePermissions();

  return (
    <DetailsPanel
      item={item}
      kind={address.kind}
      tags={tags}
      usage={reach}
      reachHref={reachHref(workspaceId, address, reach.filters)}
      terms={model.reachTerms}
      reach={model.reach}
      // Whether an attribute is redacted is only the viewer's business when
      // they could change it.
      withSensitivity={address.kind === 'attribute' && hasPermission('journeys/attribute_metadata/edit')}
      campaignIndefinite={model.campaignIndefinite}
    />
  );
}
